import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Paper, Card, CardContent, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, Chip, Tooltip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, IconButton, Switch,
  FormControlLabel, Tabs, Tab, LinearProgress, Snackbar,
} from '@mui/material';
import {
  Calculate, Settings, CheckCircle, ExpandMore, Edit, Save,
  Cancel, Refresh, Person, Group, TrendingUp, AccessTime,
  EventBusy, ArrowUpward, Info, EventAvailable, FreeBreakfast,
  EventRepeat, Warning,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  useLazyPreviewAnnualLeaveQuery,
  useApplyAnnualLeaveMutation,
  useEndOfYearAnnualLeaveMutation,
  useGetLeaveConfigQuery,
  useUpdateLeaveConfigMutation,
} from '../../../slices/annualLeaveApiSlice';
import { useGetUsersQuery, useGetStaffListQuery } from '../../../slices/usersApiSlice';
import { useLayout } from '../../../context/LayoutContext';






const fmtHrs = (h) => {
  if (h === null || h === undefined || h === '' || Number.isNaN(Number(h))) return '—';
  const totalMinutes = Math.round(Number(h) * 60);
  const sign = totalMinutes < 0 ? '-' : '';
  const abs = Math.abs(totalMinutes);
  const hrs = Math.floor(abs / 60);
  const mins = abs % 60;
  return `${sign}${hrs}:${String(mins).padStart(2, '0')}`;
};

const toLocalISO = (d) => {
  const dd = new Date(d);
  return `${dd.getFullYear()}-${String(dd.getMonth()+1).padStart(2,'0')}-${String(dd.getDate()).padStart(2,'0')}`;
};

// Colour a method chip based on which method won
const methodColor = (label) => {
  if (!label) return 'default';
  if (label.includes('No entitlement')) return 'default';
  if (label.includes('Method 1')) return 'success';
  if (label.includes('Method 2')) return 'info';
  return 'warning';
};

// Given a tier list and a gross duration, find the applicable break deduction.
// Mirrors the backend's getBreakDeductionHours logic exactly, so the preview
// in the UI always matches what the server will actually calculate.
const deductionForTiers = (tiers, grossHours) => {
  if (!Array.isArray(tiers) || tiers.length === 0) return 0;
  const sorted = [...tiers].sort((a, b) => a.uptoHours - b.uptoHours);
  for (const tier of sorted) {
    if (grossHours <= tier.uptoHours) return tier.breakHours ?? 0;
  }
  return sorted[sorted.length - 1].breakHours ?? 0;
};


function BreakTierEditor({ title, tiers, editable, onChangeTier, onAddTier, onRemoveTier, compact }) {
  const sample = [3, 4, 5, 6, 7, 8, 10, 12];
  return (
    <Box>
      {title && <Typography variant="subtitle2" gutterBottom>{title}</Typography>}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        {tiers.map((tier, idx) => (
          <Grid item xs={12} sm={6} md={compact ? 4 : 3} key={idx}>
            <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small" type="number" label="Up to (hrs)"
                    value={tier.uptoHours} disabled={!editable}
                    onChange={(e) => onChangeTier(idx, 'uptoHours', e.target.value)}
                    inputProps={{ min: 0, max: 24, step: 0.25 }}
                  />
                  <Typography variant="caption" color="text.secondary">= {fmtHrs(tier.uptoHours)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small" type="number" label="Break (hrs)"
                    value={tier.breakHours} disabled={!editable}
                    onChange={(e) => onChangeTier(idx, 'breakHours', e.target.value)}
                    inputProps={{ min: 0, max: 4, step: 0.25 }}
                  />
                  <Typography variant="caption" color="text.secondary">= {fmtHrs(tier.breakHours)}</Typography>
                </Grid>
              </Grid>
              {editable && tiers.length > 1 && (
                <Button size="small" color="error" sx={{ mt: 1 }} onClick={() => onRemoveTier(idx)}>
                  Remove Tier
                </Button>
              )}
              {idx === tiers.length - 1 && (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                  Also applies to anything above this
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
      {editable && <Button size="small" onClick={onAddTier} sx={{ mb: 2 }}>+ Add Tier</Button>}
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Preview</Typography>
      <Grid container spacing={1}>
        {sample.map((g) => {
          const ded = deductionForTiers(tiers, g);
          return (
            <Grid item xs={6} sm={3} md={1.5} key={g}>
              <Box sx={{ p: 1, border: '1px solid #eee', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">{fmtHrs(g)} gross</Typography>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                  {'\u2212'}{fmtHrs(ded)} {'\u2192'} {fmtHrs(Math.max(0, g - ded))}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}



export default function AnnualLeaveCalcScreen() {
  const { setFluid } = useLayout();
  const { userInfo } = useSelector((s) => s.auth);
  const isReporter = userInfo?.isReporter === true;

  useEffect(() => { setFluid(true); return () => setFluid(false); }, [setFluid]);

  const firstOfYear = toLocalISO(new Date(new Date().getFullYear(), 0, 1));
  const today       = toLocalISO(new Date());
  const [startDate, setStartDate] = useState(firstOfYear);
  const [endDate,   setEndDate]   = useState(today);
  const [targetUser, setTargetUser] = useState('all');
  const [tab, setTab]              = useState(0);
  const [snack, setSnack]          = useState({ open: false, message: '', severity: 'success' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [endOfYearConfirmOpen, setEndOfYearConfirmOpen] = useState(false);
  const [detailRow,   setDetailRow]   = useState(null);
  const [editingConfig, setEditingConfig] = useState(false);
  const [localConfig,   setLocalConfig]   = useState(null);
  const [newStaffCategory, setNewStaffCategory] = useState('');
  // Keeps the Calculate tab's Start Date synced to the configured
  // Calculation Period Start Date whenever config data arrives or changes
  // (including right after it's saved on the Configuration tab, in the same
  // session, with no page reload needed) — but only until the admin edits
  // the Start Date field themselves, at which point their edit wins and this
  // stops overwriting it.
  const [startDateUserEdited, setStartDateUserEdited] = useState(false);

 // const { data: users = [] } = useGetUsersQuery();
  const { data: users = [] } = useGetStaffListQuery();

  const { data: serverConfig, isLoading: configLoading } = useGetLeaveConfigQuery();
  const [triggerPreview, { data: previewData, isFetching: previewing, error: previewError }] =
    useLazyPreviewAnnualLeaveQuery();
  const [applyAL,      { isLoading: applying }] = useApplyAnnualLeaveMutation();
  const [endOfYear,    { isLoading: rollingOver }] = useEndOfYearAnnualLeaveMutation();
  const [updateConfig, { isLoading: saving }]   = useUpdateLeaveConfigMutation();

  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  useEffect(() => {
    if (serverConfig && !localConfig) setLocalConfig(JSON.parse(JSON.stringify(serverConfig)));
  }, [serverConfig]);

  const handlePreview = () => {
    triggerPreview({
      startDate,
      endDate,
      ...(targetUser !== 'all' && { userId: targetUser }),
    });
    setTab(1);
  };


  useEffect(() => {
  if (serverConfig && !localConfig) {
    const cloned = JSON.parse(JSON.stringify(serverConfig));
    cloned.breakDeductionRules = normalizeBreakRules(cloned.breakDeductionRules);
    setLocalConfig(cloned);
  }
}, [serverConfig]);

  useEffect(() => {
    if (serverConfig && serverConfig.calculationPeriodStartDate && !startDateUserEdited) {
      setStartDate(String(serverConfig.calculationPeriodStartDate).slice(0, 10));
    }
  }, [serverConfig, startDateUserEdited]);


  const handleApply = async () => {
    setConfirmOpen(false);
    try {
      const userIds = targetUser !== 'all'
        ? [targetUser]
        : previewData?.results?.map((r) => r.userId) || [];
      const res = await applyAL({ startDate, endDate, userIds }).unwrap();
      showSnack(`Applied to ${res.applied?.length} staff member(s)`, 'success');
      if (res.errors?.length > 0) {
        console.error('Apply errors:', res.errors);
        showSnack(`${res.errors.length} error(s) — check console`, 'warning');
      }
    } catch (err) {
      showSnack(err?.data?.message || 'Failed to apply', 'error');
    }
  };

  const handleEndOfYear = async () => {
    setEndOfYearConfirmOpen(false);
    try {
      const userIds = targetUser !== 'all' ? [targetUser] : [];
      const res = await endOfYear({ userIds }).unwrap();
      showSnack(`End of Year complete for ${res.applied?.length} staff member(s)`, 'success');
      if (res.errors?.length > 0) {
        console.error('End of Year errors:', res.errors);
        showSnack(`${res.errors.length} error(s) — check console`, 'warning');
      }
    } catch (err) {
      showSnack(err?.data?.message || 'Failed to run End of Year process', 'error');
    }
  };




  // A tier's breakHours applies to any gross duration up to its own uptoHours.
// The LAST tier's breakHours also covers anything beyond every defined
// boundary (an open-ended top band), so "8-12 hours or more" just needs one
// tier at { uptoHours: 24, breakHours: 1 } — 24 matches the app's existing
// "capped at 24 hours per day" convention.
const deductionForTiers = (tiers, grossHours) => {
  if (!Array.isArray(tiers) || tiers.length === 0) return 0;
  const sorted = [...tiers].sort((a, b) => a.uptoHours - b.uptoHours);
  for (const tier of sorted) {
    if (grossHours <= tier.uptoHours) return tier.breakHours ?? 0;
  }
  return sorted[sorted.length - 1].breakHours ?? 0;
};

const DEFAULT_TIERS = [
  { uptoHours: 4, breakHours: 0 },
  { uptoHours: 6, breakHours: 0.5 },
  { uptoHours: 24, breakHours: 1 },   // 6+ hours
];

// Accepts either the new { default, byCategory } shape, or the old flat
// { shortThresholdHours, mediumBreakHours, longThresholdHours, longBreakHours }
// shape, so existing saved configs keep working after this change.
const normalizeBreakRules = (raw) => {
  if (!raw) return { default: { tiers: DEFAULT_TIERS.map((t) => ({ ...t })) }, byCategory: {} };
  if (raw.default || raw.byCategory) {
    return { default: raw.default || { tiers: DEFAULT_TIERS.map((t) => ({ ...t })) }, byCategory: raw.byCategory || {} };
  }
  const { shortThresholdHours = 4, mediumBreakHours = 0.5, longThresholdHours = 8, longBreakHours = 1 } = raw;
  return {
    default: {
      tiers: [
        { uptoHours: shortThresholdHours, breakHours: 0 },
        { uptoHours: longThresholdHours, breakHours: mediumBreakHours },
        { uptoHours: 24, breakHours: longBreakHours },
      ],
    },
    byCategory: {},
  };
};


  const handleSaveConfig = async () => {
    try {
      await updateConfig({
        accrualRate:             localConfig.accrualRate,
        method1Threshold:        localConfig.method1Threshold,
        method2MonthlyThreshold: localConfig.method2MonthlyThreshold,
        categoryHours:           localConfig.categoryHours,
        staffCategories:         localConfig.staffCategories,
        leaveTypeRules:          localConfig.leaveTypeRules,
        publicHolidayConfig:     localConfig.publicHolidayConfig,
        publicHolidays:          localConfig.publicHolidays,
        breakDeductionRules:     localConfig.breakDeductionRules,
        calculationPeriodStartDate: localConfig.calculationPeriodStartDate || null,
      }).unwrap();
      showSnack('Configuration saved', 'success');
      setEditingConfig(false);
    } catch (err) {
      showSnack(err?.data?.message || 'Failed to save config', 'error');
    }
  };

  // Only offer staff whose category is actually included in THIS calculator's
  // "All Staff (Batch)" run (configured on the Configuration tab / the shared
  // Annual Leave Configuration screen) — picking someone outside that list
  // here would be misleading, since Apply would never touch them in a batch run.
  const eligibleUsers = users.filter((u) =>
    (serverConfig?.staffCategories || []).includes(u.jobtitle)
  );

  const results = previewData?.results || [];

  // ── DataGrid columns ───────────────────────────────────────────────────────
  const columns = [
    {
      field: 'name', headerName: 'Staff Member', width: 165,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person fontSize="small" color="action" />
          <Typography variant="body2">{p.value}</Typography>
        </Box>
      ),
    },
    { field: 'jobtitle', headerName: 'Category', width: 125 },
    {
      field: 'isPartTime', headerName: 'PT', width: 60, align: 'center', headerAlign: 'center',
      renderCell: (p) => p.value
        ? <Chip label="PT" size="small" color="warning" variant="outlined" />
        : null,
    },
    {
      field: 'hoursWorked', headerName: 'Hrs Worked', width: 110, type: 'number',
      renderCell: (p) => <Typography variant="body2">{fmtHrs(p.value)}</Typography>,
    },
    {
      field: 'qualifyingLeaveHours', headerName: 'Qualifying Leave', width: 125, type: 'number',
      renderCell: (p) => <Typography variant="body2" color="info.main">{fmtHrs(p.value)}</Typography>,
    },
    {
      field: 'totalHoursBase', headerName: 'Total Base Hrs', width: 120, type: 'number',
      renderCell: (p) => <Typography variant="body2" fontWeight={600}>{fmtHrs(p.value)}</Typography>,
    },
    {
      field: 'methodUsed', headerName: 'Method Used', width: 190,
      renderCell: (p) => (
        <Chip label={p.value} size="small" color={methodColor(p.value)} variant="outlined" />
      ),
    },
    {
      field: 'statutoryAL', headerName: 'Statutory AL', width: 110, type: 'number',
      renderCell: (p) => <Typography variant="body2" color="success.main">{fmtHrs(p.value)}</Typography>,
    },
    {
      field: 'publicHolidayHours', headerName: 'Public Holiday', width: 120, type: 'number',
      renderCell: (p) => <Typography variant="body2" color="secondary.main">{fmtHrs(p.value)}</Typography>,
    },
    {
      field: 'totalNewAL', headerName: 'AL Earned', width: 110, type: 'number',
      renderCell: (p) => <Typography variant="body2" color="success.main" fontWeight={600}>{fmtHrs(p.value)}</Typography>,
    },
    {
      field: 'alTakenHours', headerName: 'AL Taken', width: 105, type: 'number',
      renderCell: (p) => <Typography variant="body2" color="error.main">{fmtHrs(p.value)}</Typography>,
    },
    {
      field: 'bringForward', headerName: 'Carry-Over', width: 105, type: 'number',
      renderCell: (p) => <Typography variant="body2" color="text.secondary">{fmtHrs(p.value)}</Typography>,
    },
    {
      field: 'trueBalance', headerName: 'True Balance', width: 125, type: 'number',
      renderCell: (p) => (
        <Chip
          label={fmtHrs(p.value)}
          size="small"
          color={p.value >= 0 ? 'success' : 'error'}
          variant="filled"
        />
      ),
    },
    {
      field: 'actions', headerName: '', width: 80, sortable: false,
      renderCell: (p) => (
        <Button size="small" onClick={() => setDetailRow(p.row)}>Detail</Button>
      ),
    },
  ];

  const rows = results.map((r) => ({ ...r, id: r.userId }));

  const totalWorked   = results.reduce((s, r) => s + (r.hoursWorked          || 0), 0);
  const totalStatAL   = results.reduce((s, r) => s + (r.statutoryAL         || 0), 0);
  const totalPH       = results.reduce((s, r) => s + (r.publicHolidayHours  || 0), 0);
  const totalNewAL    = results.reduce((s, r) => s + (r.totalNewAL          || 0), 0);
  const totalALTaken  = results.reduce((s, r) => s + (r.alTakenHours        || 0), 0);
  const totalTrue     = results.reduce((s, r) => s + (r.trueBalance         || 0), 0);

  // Config helpers
  const toggleStaffCategory = (category) => {
    setLocalConfig((prev) => {
      const current = prev.staffCategories || [];
      const next = current.includes(category)
        ? current.filter((c) => c !== category)
        : [...current, category];
      return { ...prev, staffCategories: next };
    });
  };
  const addCustomStaffCategory = (category) => {
    const trimmed = (category || '').trim();
    if (!trimmed) return;
    setLocalConfig((prev) => {
      const current = prev.staffCategories || [];
      return current.includes(trimmed) ? prev : { ...prev, staffCategories: [...current, trimmed] };
    });
  };
  const updateCatHours = (category, weeklyHours) => {
    setLocalConfig((prev) => ({
      ...prev,
      categoryHours: prev.categoryHours.map((c) =>
        c.category === category ? { ...c, weeklyHours: Number(weeklyHours) } : c
      ),
    }));
  };
  const updateLeaveRule = (reason, field, value) => {
    setLocalConfig((prev) => ({
      ...prev,
      leaveTypeRules: prev.leaveTypeRules.map((r) =>
        r.reason === reason ? { ...r, [field]: value } : r
      ),
    }));
  };
  const updatePHConfig = (field, value) => {
    setLocalConfig((prev) => ({
      ...prev,
      publicHolidayConfig: { ...prev.publicHolidayConfig, [field]: value },
    }));
  };
  

  const updateDefaultTierField = (idx, field, value) => {
  setLocalConfig((prev) => {
    const tiers = [...(prev.breakDeductionRules?.default?.tiers || DEFAULT_TIERS)];
    tiers[idx] = { ...tiers[idx], [field]: Number(value) };
    return { ...prev, breakDeductionRules: { ...prev.breakDeductionRules, default: { tiers } } };
  });
};
const addDefaultTier = () => {
  setLocalConfig((prev) => {
    const tiers = [...(prev.breakDeductionRules?.default?.tiers || DEFAULT_TIERS)];
    const lastUpto = tiers.length ? tiers[tiers.length - 1].uptoHours : 0;
    tiers.push({ uptoHours: lastUpto + 2, breakHours: 1 });
    return { ...prev, breakDeductionRules: { ...prev.breakDeductionRules, default: { tiers } } };
  });
};
const removeDefaultTier = (idx) => {
  setLocalConfig((prev) => ({
    ...prev,
    breakDeductionRules: {
      ...prev.breakDeductionRules,
      default: { tiers: (prev.breakDeductionRules?.default?.tiers || DEFAULT_TIERS).filter((_, i) => i !== idx) },
    },
  }));
};

const updateCategoryTierField = (category, idx, field, value) => {
  setLocalConfig((prev) => {
    const byCategory = { ...(prev.breakDeductionRules?.byCategory || {}) };
    const tiers = [...(byCategory[category]?.tiers || [])];
    tiers[idx] = { ...tiers[idx], [field]: Number(value) };
    byCategory[category] = { tiers };
    return { ...prev, breakDeductionRules: { ...prev.breakDeductionRules, byCategory } };
  });
};
const addCategoryTier = (category) => {
  setLocalConfig((prev) => {
    const byCategory = { ...(prev.breakDeductionRules?.byCategory || {}) };
    const tiers = [...(byCategory[category]?.tiers || [])];
    const lastUpto = tiers.length ? tiers[tiers.length - 1].uptoHours : 0;
    tiers.push({ uptoHours: lastUpto + 2, breakHours: 1 });
    byCategory[category] = { tiers };
    return { ...prev, breakDeductionRules: { ...prev.breakDeductionRules, byCategory } };
  });
};
const removeCategoryTier = (category, idx) => {
  setLocalConfig((prev) => {
    const byCategory = { ...(prev.breakDeductionRules?.byCategory || {}) };
    byCategory[category] = { tiers: (byCategory[category]?.tiers || []).filter((_, i) => i !== idx) };
    return { ...prev, breakDeductionRules: { ...prev.breakDeductionRules, byCategory } };
  });
};
const enableCategoryOverride = (category) => {
  setLocalConfig((prev) => {
    const byCategory = { ...(prev.breakDeductionRules?.byCategory || {}) };
    byCategory[category] = { tiers: (prev.breakDeductionRules?.default?.tiers || DEFAULT_TIERS).map((t) => ({ ...t })) };
    return { ...prev, breakDeductionRules: { ...prev.breakDeductionRules, byCategory } };
  });
};
const removeCategoryOverride = (category) => {
  setLocalConfig((prev) => {
    const byCategory = { ...(prev.breakDeductionRules?.byCategory || {}) };
    delete byCategory[category];
    return { ...prev, breakDeductionRules: { ...prev.breakDeductionRules, byCategory } };
  });
};


  const updateHolidayField = (idx, field, value) => {
    setLocalConfig((prev) => ({
      ...prev,
      publicHolidays: prev.publicHolidays.map((h, i) =>
        i === idx ? { ...h, [field]: value } : h
      ),
    }));
  };
  const removeHoliday = (idx) => {
    setLocalConfig((prev) => ({
      ...prev,
      publicHolidays: prev.publicHolidays.filter((_, i) => i !== idx),
    }));
  };
  const addHoliday = () => {
    setLocalConfig((prev) => ({
      ...prev,
      publicHolidays: [...(prev.publicHolidays || []), { date: '', name: '' }],
    }));
  };

  // Live example for the break-rules panel, driven by whatever is currently
  // in localConfig (or serverConfig when not editing), so admins can see the
  // effect of a change before saving.
  
  

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Calculate sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h4">Annual Leave Calculator</Typography>
          </Box>
          <Typography variant="subtitle2" color="text.secondary">
            Irish Statutory Calculation — Organisation of Working Time Act 1997 · Admin Panel
          </Typography>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Calculate"                                           icon={<Calculate />}    iconPosition="start" />
        <Tab label={`Results ${results.length > 0 ? `(${results.length})` : ''}`} icon={<TrendingUp />}   iconPosition="start" />
        <Tab label="Configuration"                                       icon={<Settings />}     iconPosition="start" />
      </Tabs>

      {/* ══════════════ TAB 0: CALCULATE ══════════════ */}
      {tab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTime color="primary" /> Calculation Period
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField fullWidth label="Start Date" type="date"
                    value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setStartDateUserEdited(true); }}
                    InputLabelProps={{ shrink: true }} size="small" />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="End Date" type="date"
                    value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }} size="small" />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Staff Member</InputLabel>
                    <Select value={targetUser} label="Staff Member" onChange={(e) => setTargetUser(e.target.value)}>
                          <MenuItem value="all">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Group fontSize="small" /> All Staff (Batch — {eligibleUsers.length} staff)
                            </Box>
                          </MenuItem>
                          {[...eligibleUsers]
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .map((u) => (
                              <MenuItem key={u._id} value={u._id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Person fontSize="small" />
                                  {u.name}
                                  {u.jobtitle && (
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                      {u.jobtitle}
                                    </Typography>
                                  )}
                                  {u.isPartTime && (
                                    <Chip label="PT" size="small" color="warning" variant="outlined" sx={{ ml: 1 }} />
                                  )}
                                </Box>
                              </MenuItem>
                            ))}
                        </Select>

                  </FormControl>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3 }}>
                <Button variant="contained" startIcon={<Calculate />}
                  onClick={handlePreview} disabled={previewing} fullWidth>
                  {previewing ? 'Calculating…' : 'Preview Calculation'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Formula explanation */}
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Info color="primary" /> Irish Statutory Methods
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                The <strong>highest</strong> result from the three annual leave methods is used
                (OWT Act 1997, s.19), then <strong>Public Holiday entitlement</strong> (s.21) is
                added on top.
              </Alert>
              {[
                {
                  label: 'Method 1 — Full-Time',
                  desc: '≥ 1,365 hours in the leave year → 4 working weeks',
                  color: 'success.main',
                },
                {
                  label: 'Method 2 — Regular Part-Time',
                  desc: '1/3 of a working week for each month with ≥ 117 hours worked',
                  color: 'info.main',
                },
                {
                  label: 'Method 3 — 8% Hourly Rate',
                  desc: '8% of total hours (worked + qualifying leave), capped at 4 working weeks',
                  color: 'warning.main',
                },
                {
                  label: '+ Public Holidays — Extra AL',
                  desc: 'Full-time: Weekly Hours ÷ Working Days per holiday. Part-time: average daily hours worked over reference period (after meeting 40hr/5wk eligibility test)',
                  color: 'secondary.main',
                },
              ].map(({ label, desc, color }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, mt: 0.8, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color }}>{label}</Typography>
                    <Typography variant="caption" color="text.secondary">{desc}</Typography>
                  </Box>
                </Box>
              ))}
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                <strong>Qualifying leave</strong> (sick, maternity, adoptive etc.) is added to the hours base
                for Method 3. <strong>Overtime is included</strong> for all methods.
                Both worked shifts and qualifying leave records have the configured
                rest-break deduction applied before counting toward the hours base.
                AL taken (unavailability reason = "Annual Leave") is deducted from the final balance
                at its full (non-deducted) duration.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, border: '1px solid #f5c6a5' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventRepeat color="warning" /> End of Year Rollover
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                For the staff member selected above (or every eligible staff member, if left on "All Staff"),
                copies their current <strong>Opening Balance</strong> into <strong>Bring Forward</strong> and
                resets Opening Balance to <strong>0</strong>. Run this once, after the final Apply of the
                outgoing leave year, so next year's calculations start from a clean Opening Balance without
                losing whatever was left over.
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This overwrites Bring Forward. If Bring Forward for the new year also needs the next
                allowance added on top, do that separately afterwards.
              </Alert>
              <Button variant="outlined" color="warning" startIcon={<EventRepeat />}
                onClick={() => setEndOfYearConfirmOpen(true)} disabled={rollingOver}>
                {rollingOver ? <CircularProgress size={22} /> : 'Run End of Year Process'}
              </Button>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ══════════════ TAB 1: RESULTS ══════════════ */}
      {tab === 1 && (
        <Box>
          {previewing && <LinearProgress sx={{ mb: 2 }} />}
          {previewError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {previewError?.data?.message || 'Calculation failed'}
            </Alert>
          )}

          {results.length > 0 && (
            <>
              {/* Summary cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { label: 'Staff Calculated',     value: results.length,        color: 'grey',      icon: <Group /> },
                  { label: 'Total Hrs Worked',     value: fmtHrs(totalWorked),   color: 'success',   icon: <AccessTime /> },
                  { label: 'Statutory AL',         value: fmtHrs(totalStatAL),   color: 'info',      icon: <TrendingUp /> },
                  { label: 'Public Holiday AL',    value: fmtHrs(totalPH),       color: 'secondary', icon: <EventAvailable /> },
                  { label: 'Total AL Earned',      value: fmtHrs(totalNewAL),    color: 'primary',   icon: <TrendingUp /> },
                  { label: 'Total AL Taken',       value: fmtHrs(totalALTaken),  color: 'error',     icon: <EventBusy /> },
                  { label: 'Total True Balance',   value: fmtHrs(totalTrue),     color: 'success',   icon: <ArrowUpward /> },
                ].map(({ label, value, color, icon }) => (
                  <Grid item xs={6} sm={4} md={true} key={label}>
                    <Card elevation={1} sx={{ border: '1px solid #e0e0e0', height: '100%' }}>
                      <CardContent sx={{ p: '12px !important' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, color: `${color}.main` }}>
                          {React.cloneElement(icon, { sx: { fontSize: 18 } })}
                          <Typography variant="h6" sx={{ color: `${color}.main`, fontWeight: 700, lineHeight: 1 }}>
                            {value}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {!isReporter && (
                    <Button variant="contained" color="success" startIcon={<CheckCircle />}
                      onClick={() => setConfirmOpen(true)} disabled={applying}>
                      {applying ? 'Applying…' : `Apply to ${targetUser === 'all' ? 'All Staff' : '1 Staff Member'}`}
                    </Button>
                  )}
                  <Button variant="outlined" startIcon={<Refresh />} onClick={handlePreview} disabled={previewing}>
                    Recalculate
                  </Button>
                  {isReporter ? (
                    <Alert severity="info" sx={{ py: 0.5, flex: 1 }}>
                      View only — results are not saved. Contact an admin to apply this calculation.
                    </Alert>
                  ) : (
                    <Alert severity="warning" sx={{ py: 0.5, flex: 1 }}>
                      Applying will update <strong>Opening Balance</strong>. Carry-over (Bring Forward) is never changed.
                    </Alert>
                  )}
                </Box>

              <Box sx={{ height: 640 }}>
                <DataGrid
                  density="compact"
                  rows={rows}
                  columns={columns}
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{ toolbar: { showQuickFilter: true } }}
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{ pagination: { pageSize: 25 } }}
                  disableRowSelectionOnClick
                />
              </Box>
            </>
          )}

          {!previewing && results.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Calculate sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">
                Go to the Calculate tab and click Preview Calculation to see results here.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* ══════════════ TAB 2: CONFIG ══════════════ */}
      {tab === 2 && (
        <Box>
          {configLoading && <LinearProgress sx={{ mb: 2 }} />}
          {localConfig && (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  {editingConfig ? (
                    <>
                      <Button variant="contained" startIcon={<Save />} onClick={handleSaveConfig} disabled={saving}>
                        {saving ? 'Saving…' : 'Save Configuration'}
                      </Button>
                      <Button variant="outlined" startIcon={<Cancel />}
                        onClick={() => {
                        const cloned = JSON.parse(JSON.stringify(serverConfig));
                        cloned.breakDeductionRules = normalizeBreakRules(cloned.breakDeductionRules);
                        setLocalConfig(cloned);
                        setEditingConfig(false);
                      }}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    !isReporter && (
                      <Button variant="outlined" startIcon={<Edit />} onClick={() => setEditingConfig(true)}>
                        Edit Configuration
                      </Button>
                    )
                  )}
                </Box>

              <Grid container spacing={3}>
                {/* Statutory thresholds */}
                <Grid item xs={12} md={5}>
                  <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
                    <Typography variant="h6" gutterBottom>Statutory Thresholds</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Alert severity="info" sx={{ mb: 2, fontSize: '0.78rem' }}>
                      These match Organisation of Working Time Act 1997 defaults.
                      Only change if legislation is updated.
                    </Alert>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth size="small" type="number"
                          label="Method 3 — Accrual Rate (e.g. 0.08 = 8%)"
                          value={localConfig.accrualRate}
                          onChange={(e) => setLocalConfig((p) => ({ ...p, accrualRate: Number(e.target.value) }))}
                          disabled={!editingConfig}
                          inputProps={{ step: 0.001, min: 0, max: 0.2 }}
                          helperText="Statutory: 0.08 (8%). Cap is always 4 working weeks."
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth size="small" type="number"
                          label="Method 1 — Full-Time Threshold (hours/year)"
                          value={localConfig.method1Threshold}
                          onChange={(e) => setLocalConfig((p) => ({ ...p, method1Threshold: Number(e.target.value) }))}
                          disabled={!editingConfig}
                          inputProps={{ step: 1, min: 0 }}
                          helperText="Statutory: 1,365 hours in the leave year → 4 working weeks"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth size="small" type="number"
                          label="Method 2 — Monthly Threshold (hours/month)"
                          value={localConfig.method2MonthlyThreshold}
                          onChange={(e) => setLocalConfig((p) => ({ ...p, method2MonthlyThreshold: Number(e.target.value) }))}
                          disabled={!editingConfig}
                          inputProps={{ step: 1, min: 0 }}
                          helperText="Statutory: 117 hours per month → 1/3 working week"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth size="small" type="date"
                          label="Calculation Period Start Date (default)"
                          InputLabelProps={{ shrink: true }}
                          value={
                            localConfig.calculationPeriodStartDate
                              ? String(localConfig.calculationPeriodStartDate).slice(0, 10)
                              : ''
                          }
                          onChange={(e) => setLocalConfig((p) => ({ ...p, calculationPeriodStartDate: e.target.value || null }))}
                          disabled={!editingConfig}
                          helperText="Pre-fills the Start Date on the Calculate tab, and is what a staff member's Profile 'Recalculate' button runs from. Leave blank to default to 1 January of the current year. Update whenever the Bring Forward cut-over date changes (e.g. an End of Year rollover mid-year)."
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Staff categories included in this calculator */}
                <Grid item xs={12}>
                  <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
                    <Typography variant="h6" gutterBottom>Staff Categories Included</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Alert severity="info" sx={{ mb: 2, fontSize: '0.78rem' }}>
                      Only staff whose category (job title) is checked below are included when
                      "All Staff (Batch)" is run on this calculator. Everyone else should instead be
                      processed on the Non Bank Annual Leave calculator.
                    </Alert>
                    <Grid container spacing={0}>
                      {localConfig.categoryHours.map((c) => (
                        <Grid item xs={6} sm={4} md={3} key={c.category}>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={(localConfig.staffCategories || []).includes(c.category)}
                                onChange={() => toggleStaffCategory(c.category)}
                                disabled={!editingConfig}
                              />
                            }
                            label={c.category}
                          />
                        </Grid>
                      ))}
                      {(localConfig.staffCategories || [])
                        .filter((cat) => !localConfig.categoryHours.some((c) => c.category === cat))
                        .map((cat) => (
                          <Grid item xs={6} sm={4} md={3} key={cat}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked
                                  onChange={() => toggleStaffCategory(cat)}
                                  disabled={!editingConfig}
                                />
                              }
                              label={`${cat} (custom)`}
                            />
                          </Grid>
                        ))}
                    </Grid>
                    {editingConfig && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <TextField
                          size="small" label="Add custom category"
                          value={newStaffCategory}
                          onChange={(e) => setNewStaffCategory(e.target.value)}
                        />
                        <Button
                          variant="outlined"
                          onClick={() => { addCustomStaffCategory(newStaffCategory); setNewStaffCategory(''); }}
                        >
                          Add
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Grid>

                {/* Category weekly hours */}
                <Grid item xs={12} md={7}>
                  <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
                    <Typography variant="h6" gutterBottom>Weekly Hours by Category</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Used to calculate the 4-week cap, convert weekly caps (e.g. 13 weeks
                      Carer's Leave) into hours, and to compute Public Holiday hours-per-day.
                    </Typography>
                    <Grid container spacing={2}>
                      {localConfig.categoryHours.map((cat) => (
                        <Grid item xs={6} sm={4} key={cat.category}>
                          <TextField
                            fullWidth label={cat.category} type="number" size="small"
                            value={cat.weeklyHours}
                            onChange={(e) => updateCatHours(cat.category, e.target.value)}
                            disabled={!editingConfig}
                            inputProps={{ step: 0.5, min: 1, max: 80 }}
                            InputProps={{
                              endAdornment: <Typography variant="caption" sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>hrs/wk</Typography>,
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>

                {/* Break deduction rules */}
                <Grid item xs={12}>
                    <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FreeBreakfast color="primary" /> Break Deduction Rules
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                        Applied to the gross clocked length of a shift, and to the gross duration of any leave type
                        marked "Qualifies / Accrues" below. Each job category can have its own tiers; categories with
                        no custom rule use the Default below. Tiers can be added or removed freely — this is a
                        business policy, not a legislative requirement.
                      </Alert>

                      <BreakTierEditor
                        title="Default (all other staff)"
                        tiers={localConfig.breakDeductionRules?.default?.tiers || DEFAULT_TIERS}
                        editable={editingConfig}
                        onChangeTier={updateDefaultTierField}
                        onAddTier={addDefaultTier}
                        onRemoveTier={removeDefaultTier}
                      />

                      <Divider sx={{ my: 3 }} />
                      <Typography variant="subtitle2" gutterBottom>Category-Specific Rules</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        One row per job category. A category with no custom rule inherits the Default above.
                      </Typography>

                      {localConfig.categoryHours.map((cat) => {
                        const override = localConfig.breakDeductionRules?.byCategory?.[cat.category];
                        return (
                          <Box key={cat.category} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{cat.category}</Typography>
                              {override
                                ? <Chip label="Custom rule" size="small" color="warning" variant="outlined" />
                                : <Chip label="Uses Default" size="small" variant="outlined" />}
                              {editingConfig && (override
                                ? <Button size="small" color="error" onClick={() => removeCategoryOverride(cat.category)}>Remove Custom Rule</Button>
                                : <Button size="small" onClick={() => enableCategoryOverride(cat.category)}>Customize for {cat.category}</Button>
                              )}
                            </Box>
                            {override && (
                              <BreakTierEditor
                                tiers={override.tiers}
                                editable={editingConfig}
                                onChangeTier={(idx, field, value) => updateCategoryTierField(cat.category, idx, field, value)}
                                onAddTier={() => addCategoryTier(cat.category)}
                                onRemoveTier={(idx) => removeCategoryTier(cat.category, idx)}
                                compact
                              />
                            )}
                          </Box>
                        );
                      })}
                    </Paper>
                  </Grid>

                {/* Leave type rules */}
                <Grid item xs={12}>
                  <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
                    <Typography variant="h6" gutterBottom>Leave Type Accrual Rules</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      <strong>Qualifying hours</strong> are added to the hours base for Method 3
                      (net of the break deduction above). <strong>Weeks cap</strong> is converted
                      to hours using the staff member's weekly hours, and applied after the break
                      deduction. Set weeks cap to <strong>0 or blank</strong> for unlimited
                      qualifying hours.
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell sx={{ fontWeight: 700 }}>Leave Type</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="center">Qualifies / Accrues?</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="center">Weeks Cap</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {localConfig.leaveTypeRules.map((rule) => (
                            <TableRow key={rule.reason} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{rule.reason}</Typography>
                              </TableCell>
                              <TableCell align="center">
                                {editingConfig ? (
                                  <Switch
                                    checked={rule.accrue}
                                    size="small"
                                    color="success"
                                    onChange={(e) => updateLeaveRule(rule.reason, 'accrue', e.target.checked)}
                                  />
                                ) : (
                                  <Chip
                                    label={rule.accrue ? 'Yes' : 'No'}
                                    size="small"
                                    color={rule.accrue ? 'success' : 'default'}
                                    variant="outlined"
                                  />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {rule.accrue ? (
                                  editingConfig ? (
                                    <TextField
                                      type="number" size="small"
                                      value={rule.weeksCap === null ? '' : rule.weeksCap}
                                      onChange={(e) => updateLeaveRule(
                                        rule.reason, 'weeksCap',
                                        e.target.value === '' ? null : Number(e.target.value)
                                      )}
                                      inputProps={{ min: 0, max: 104 }}
                                      sx={{ width: 80 }}
                                      placeholder="∞"
                                    />
                                  ) : (
                                    <Typography variant="body2">
                                      {rule.weeksCap === null || rule.weeksCap === 0
                                        ? '∞ Unlimited'
                                        : `${rule.weeksCap} wks`}
                                    </Typography>
                                  )
                                ) : (
                                  <Typography variant="caption" color="text.disabled">N/A</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {editingConfig ? (
                                  <TextField
                                    size="small" fullWidth value={rule.notes || ''}
                                    onChange={(e) => updateLeaveRule(rule.reason, 'notes', e.target.value)}
                                    placeholder="Notes…"
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.secondary">{rule.notes}</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>

                {/* Public Holiday Configuration */}
                <Grid item xs={12}>
                  <Paper elevation={1} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EventAvailable color="secondary" />
                      Public Holiday Entitlement (Extra Day of Annual Leave)
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                      Organisation of Working Time Act 1997, s.21. Full-time and fixed-hours staff
                      receive (Weekly Hours ÷ Working Days/Week) per qualifying holiday. Part-time
                      staff who meet the eligibility test instead receive their average daily
                      hours actually worked over the configured reference period — only days
                      actually worked count toward the average.
                    </Alert>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={localConfig.publicHolidayConfig?.enabled !== false}
                              disabled={!editingConfig}
                              onChange={(e) => updatePHConfig('enabled', e.target.checked)}
                            />
                          }
                          label="Enabled"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth size="small" type="number" label="Working Days / Week"
                          value={localConfig.publicHolidayConfig?.workingDaysPerWeek ?? 5}
                          disabled={!editingConfig}
                          onChange={(e) => updatePHConfig('workingDaysPerWeek', Number(e.target.value))}
                          inputProps={{ min: 1, max: 7, step: 1 }}
                          helperText="Converts weekly hours → hours per holiday"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth size="small" type="number" label="Part-Time Lookback (weeks)"
                          value={localConfig.publicHolidayConfig?.partTimeLookbackWeeks ?? 5}
                          disabled={!editingConfig}
                          onChange={(e) => updatePHConfig('partTimeLookbackWeeks', Number(e.target.value))}
                          inputProps={{ min: 1, max: 26, step: 1 }}
                          helperText="Statutory: 5 weeks"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth size="small" type="number" label="Part-Time Threshold (hours)"
                          value={localConfig.publicHolidayConfig?.partTimeThresholdHours ?? 40}
                          disabled={!editingConfig}
                          onChange={(e) => updatePHConfig('partTimeThresholdHours', Number(e.target.value))}
                          inputProps={{ min: 1, max: 200, step: 1 }}
                          helperText="Statutory: 40 hours"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth size="small" type="number" label="Average Hours Lookback (weeks)"
                          value={localConfig.publicHolidayConfig?.averageHoursLookbackWeeks ?? 13}
                          disabled={!editingConfig}
                          onChange={(e) => updatePHConfig('averageHoursLookbackWeeks', Number(e.target.value))}
                          inputProps={{ min: 1, max: 52, step: 1 }}
                          helperText="Reference period for part-time average daily hours (benefit amount, separate from eligibility)"
                        />
                      </Grid>
                    </Grid>

                    <Typography variant="subtitle2" gutterBottom>Public Holiday Dates</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Irish public holiday dates change year to year (e.g. first/last Monday
                      of a month) — review and update this list annually.
                    </Typography>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                            {editingConfig && <TableCell align="center" sx={{ fontWeight: 700 }}>Remove</TableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(localConfig.publicHolidays || []).map((ph, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                {editingConfig ? (
                                  <TextField
                                    type="date" size="small" value={ph.date}
                                    onChange={(e) => updateHolidayField(idx, 'date', e.target.value)}
                                  />
                                ) : (
                                  ph.date
                                    ? new Date(ph.date).toLocaleDateString('en-IE', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })
                                    : '—'
                                )}
                              </TableCell>
                              <TableCell>
                                {editingConfig ? (
                                  <TextField
                                    size="small" fullWidth value={ph.name}
                                    onChange={(e) => updateHolidayField(idx, 'name', e.target.value)}
                                  />
                                ) : (
                                  <Typography variant="body2">{ph.name}</Typography>
                                )}
                              </TableCell>
                              {editingConfig && (
                                <TableCell align="center">
                                  <Button size="small" color="error" onClick={() => removeHoliday(idx)}>
                                    Remove
                                  </Button>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                          {(!localConfig.publicHolidays || localConfig.publicHolidays.length === 0) && (
                            <TableRow>
                              <TableCell colSpan={editingConfig ? 3 : 2}>
                                <Typography variant="caption" color="text.disabled">No holidays configured.</Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {editingConfig && (
                      <Button size="small" sx={{ mt: 2 }} onClick={addHoliday}>
                        + Add Holiday
                      </Button>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      )}

      {/* ── Confirm Apply Dialog ── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Apply Annual Leave Calculation</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will <strong>permanently update</strong> the Opening Balance for{' '}
            {targetUser === 'all'
              ? <strong>all {results.length} staff members</strong>
              : <strong>1 staff member</strong>}.
            Bring Forward (carry-over) is never changed.
          </Alert>
          <Typography variant="body2"><strong>Period:</strong> {startDate} to {endDate}</Typography>
          {results.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2"><strong>Total statutory AL:</strong> {fmtHrs(totalStatAL)}</Typography>
              <Typography variant="body2"><strong>Total public holiday AL:</strong> {fmtHrs(totalPH)}</Typography>
              <Typography variant="body2"><strong>Total AL taken:</strong> {fmtHrs(totalALTaken)}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApply} startIcon={<CheckCircle />}>
            Confirm Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── End of Year Confirmation Dialog ── */}
      <Dialog open={endOfYearConfirmOpen} onClose={() => setEndOfYearConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" /> Confirm End of Year Rollover
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            This will copy the current Opening Balance into Bring Forward, and reset Opening Balance to 0,
            for{' '}
            <strong>
              {targetUser === 'all'
                ? `all ${eligibleUsers.length} eligible staff member(s)`
                : eligibleUsers.find((u) => u._id === targetUser)?.name || 'the selected staff member'}
            </strong>.
          </Typography>
          <Alert severity="warning">
            This overwrites each staff member's existing Bring Forward value with their current Opening
            Balance — it does not add to it. Make sure the final Preview/Apply for the outgoing year has
            already been run before doing this.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndOfYearConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleEndOfYear}>Confirm &amp; Run</Button>
        </DialogActions>
      </Dialog>

      {/* ── Detail Dialog ── */}
      <Dialog open={!!detailRow} onClose={() => setDetailRow(null)} maxWidth="md" fullWidth>
        {detailRow && (
          <>
            <DialogTitle>
              AL Breakdown — {detailRow.name}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {detailRow.jobtitle} · {detailRow.weeklyHours} hrs/week · 4-week cap = {fmtHrs(detailRow.fourWeeksCap)}
                {detailRow.isPartTime && (
                  <Chip label="Part-Time" size="small" color="warning" variant="outlined" sx={{ ml: 1 }} />
                )}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              {/* Three-method comparison */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }}>
                <Typography variant="subtitle2" gutterBottom>Three-Method Comparison</Typography>
                <Grid container spacing={2}>
                  {[
                    {
                      label:  'Method 1 — Full-Time',
                      value:  detailRow.method1Hours,
                      note:   `Base: ${fmtHrs(detailRow.totalHoursBase)} (threshold: ${previewData?.config?.method1Threshold || 1365} hrs)`,
                      color:  'success',
                    },
                    {
                      label:  'Method 2 — Part-Time',
                      value:  detailRow.method2Hours,
                      note:   `${detailRow.method2Months} qualifying month(s) × ${fmtHrs(detailRow.weeklyHours / 3)}`,
                      color:  'info',
                    },
                    {
                      label:  'Method 3 — 8%',
                      value:  detailRow.method3Hours,
                      note:   `${fmtHrs(detailRow.totalHoursBase)} × ${((previewData?.config?.accrualRate || 0.08) * 100).toFixed(1)}% = ${fmtHrs(detailRow.method3Raw)} (capped at ${fmtHrs(detailRow.fourWeeksCap)})`,
                      color:  'warning',
                    },
                  ].map(({ label, value, note, color }) => {
                    const isWinner = detailRow.statutoryAL > 0 && fmtHrs(value) === fmtHrs(detailRow.statutoryAL);
                    return (
                      <Grid item xs={12} sm={4} key={label}>
                        <Box sx={{
                          p: 1.5, border: `2px solid`,
                          borderColor: isWinner ? `${color}.main` : 'grey.300',
                          borderRadius: 1, textAlign: 'center',
                          bgcolor: isWinner ? `${color}.50` : 'white',
                        }}>
                          {isWinner && (
                            <Chip label="✓ Used" size="small" color={color} sx={{ mb: 0.5 }} />
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
                          <Typography variant="h6" color={`${color}.main`}>{fmtHrs(value)}</Typography>
                          <Typography variant="caption" color="text.secondary">{note}</Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Statutory AL (best method)</Typography>
                    <Typography variant="h6" color="success.main">{fmtHrs(detailRow.statutoryAL)}</Typography>
                  </Box>
                  <Typography variant="h5" color="text.disabled">+</Typography>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Public Holiday AL</Typography>
                    <Typography variant="h6" color="secondary.main">{fmtHrs(detailRow.publicHolidayHours)}</Typography>
                  </Box>
                  <Typography variant="h5" color="text.disabled">=</Typography>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Total AL Earned</Typography>
                    <Typography variant="h6" color="primary.main">{fmtHrs(detailRow.totalNewAL)}</Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Balance summary */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { label: 'Hours Worked',          value: fmtHrs(detailRow.hoursWorked),          color: 'text.primary' },
                  { label: 'Qualifying Leave Hrs',   value: fmtHrs(detailRow.qualifyingLeaveHours), color: 'info.main' },
                  { label: 'Total Base Hours',       value: fmtHrs(detailRow.totalHoursBase),       color: 'text.primary' },
                  { label: 'Public Holiday AL',      value: fmtHrs(detailRow.publicHolidayHours),   color: 'secondary.main' },
                  { label: 'AL Earned (total)',      value: fmtHrs(detailRow.totalNewAL),           color: 'success.main' },
                  { label: 'AL Taken',               value: `-${fmtHrs(detailRow.alTakenHours)}`,   color: 'error.main' },
                  { label: 'Opening Balance',        value: fmtHrs(detailRow.newOpeningBalance),    color: 'primary.main' },
                  { label: 'Carry-Over (BF)',        value: fmtHrs(detailRow.bringForward),         color: 'text.secondary' },
                  { label: 'True Balance',           value: fmtHrs(detailRow.trueBalance),          color: 'success.main' },
                ].map(({ label, value, color }) => (
                  <Grid item xs={6} sm={4} md={3} key={label}>
                    <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, textAlign: 'center', height: '100%' }}>
                      <Typography variant="body2" color={color} sx={{ fontWeight: 700 }}>{value}</Typography>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Shift breakdown */}
              <Accordion defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">
                    Shift Breakdown ({detailRow.shiftBreakdown?.length || 0} shifts)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer sx={{ maxHeight: 280 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell>Date</TableCell>
                          <TableCell>Ward</TableCell>
                          <TableCell align="right">Gross Hrs</TableCell>
                          <TableCell align="right">Break Deducted</TableCell>
                          <TableCell align="right">Net Hrs</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(detailRow.shiftBreakdown || []).map((s, i) => (
                          <TableRow key={i}>
                            <TableCell>{new Date(s.date).toLocaleDateString()}</TableCell>
                            <TableCell>{s.ward || '—'}</TableCell>
                            <TableCell align="right">
                              {s.grossHours !== undefined ? fmtHrs(s.grossHours) : '—'}
                            </TableCell>
                            <TableCell align="right">
                              {s.breakDeductedHours !== undefined
                                ? <Typography variant="body2" color="error.main">{'\u2212'}{fmtHrs(s.breakDeductedHours)}</Typography>
                                : '—'}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600}>{fmtHrs(s.hours)}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>

              {/* Leave breakdown */}
              <Accordion defaultExpanded sx={{ mt: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">
                    Leave Breakdown ({detailRow.leaveBreakdown?.length || 0} records)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer sx={{ maxHeight: 280 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell>Date</TableCell>
                          <TableCell>Reason</TableCell>
                          <TableCell align="right">Gross Hrs</TableCell>
                          <TableCell align="right">Break Deducted</TableCell>
                          <TableCell align="right">Net Hrs</TableCell>
                          <TableCell align="right">Capped Hrs</TableCell>
                          <TableCell align="center">Type</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(detailRow.leaveBreakdown || []).map((l, i) => (
                          <TableRow key={i}>
                            <TableCell>{new Date(l.date).toLocaleDateString()}</TableCell>
                            <TableCell>{l.reason}</TableCell>
                            <TableCell align="right">{fmtHrs(l.hours)}</TableCell>
                            <TableCell align="right">
                              {l.breakDeductedHours !== undefined
                                ? <Typography variant="body2" color="error.main">{'\u2212'}{fmtHrs(l.breakDeductedHours)}</Typography>
                                : '—'}
                            </TableCell>
                            <TableCell align="right">
                              {l.netHours !== undefined ? fmtHrs(l.netHours) : '—'}
                            </TableCell>
                            <TableCell align="right">
                              {l.cappedHours !== undefined ? fmtHrs(l.cappedHours) : '—'}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={l.type}
                                size="small"
                                color={
                                  l.type === 'deduction'  ? 'error'
                                  : l.type === 'qualifying' || l.type === 'deduction+qualifying' ? 'success'
                                  : 'default'
                                }
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>

              {/* Public Holiday breakdown */}
              <Accordion sx={{ mt: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">
                    Public Holiday Entitlement ({detailRow.publicHolidayBreakdown?.length || 0} holidays in period)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer sx={{ maxHeight: 280 }}>
                    <Table size="small">
                      <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                            <TableCell>Date</TableCell>
                            <TableCell>Holiday</TableCell>
                            <TableCell align="center">Eligible?</TableCell>
                            <TableCell align="right">Avg Hrs/Wk</TableCell>
                            <TableCell align="right">Days Worked</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell align="right">Hours Awarded</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(detailRow.publicHolidayBreakdown || []).map((ph, i) => (
                            <TableRow key={i}>
                              <TableCell>{new Date(ph.date).toLocaleDateString()}</TableCell>
                              <TableCell>{ph.name}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={ph.eligible ? 'Yes' : 'No'}
                                  size="small"
                                  color={ph.eligible ? 'success' : 'default'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right">
                                {ph.averageWeeklyHours !== undefined ? fmtHrs(ph.averageWeeklyHours) : '—'}
                              </TableCell>
                              <TableCell align="right">
                                {ph.daysWorkedInLookback !== undefined ? ph.daysWorkedInLookback : '—'}
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">{ph.reason}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" color={ph.eligible ? 'success.main' : 'text.disabled'}>
                                  {fmtHrs(ph.hours)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                          {(!detailRow.publicHolidayBreakdown || detailRow.publicHolidayBreakdown.length === 0) && (
                            <TableRow>
                              <TableCell colSpan={7}>
                                <Typography variant="caption" color="text.disabled">
                                  No public holidays fall within the selected period.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailRow(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
